import { type ReactNode, useEffect, useRef, useState } from "react";

export interface MenuItemProps {
  leading?: ReactNode;
  children: ReactNode;

  description?: ReactNode;
  trailing?: ReactNode;

  highlighted?: boolean;

  selected?: boolean;

  danger?: boolean;
  disabled?: boolean;

  size?: "sm" | "md";
}

const SCROLL_PIXELS_PER_SECOND = 20;
const MIN_SCROLL_MS = 1000;
const PAUSE_MS = 300;

/** A truncated description that slides to reveal the rest of itself while `active`, then resets. */
function ScrollingDescription({ active, children }: Readonly<{ active: boolean; children: ReactNode }>) {
  const ref = useRef<HTMLSpanElement>(null);
  const [distance, setDistance] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measures when the text itself changes, not read directly.
  useEffect(() => {
    const el = ref.current;
    if (el) setDistance(Math.max(0, el.scrollWidth - el.clientWidth));
  }, [children]);

  // Driven by the Web Animations API, not CSS keyframes, so the pause is a fixed length instead of a
  // percentage of the total duration that stretches whenever the scroll speed changes.
  useEffect(() => {
    const el = ref.current;
    if (!el || !active || distance === 0) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const scrollMs = Math.max(MIN_SCROLL_MS, (distance / SCROLL_PIXELS_PER_SECOND) * 1000);
    const total = PAUSE_MS * 3 + scrollMs * 2;
    const rest = { transform: "translateX(0)" };
    const revealed = { transform: `translateX(-${distance}px)` };

    const animation = el.animate(
      [
        { ...rest, offset: 0 },
        { ...rest, offset: PAUSE_MS / total },
        { ...revealed, offset: (PAUSE_MS + scrollMs) / total },
        { ...revealed, offset: (2 * PAUSE_MS + scrollMs) / total },
        { ...rest, offset: (2 * PAUSE_MS + 2 * scrollMs) / total },
        { ...rest, offset: 1 },
      ],
      { duration: total, iterations: Number.POSITIVE_INFINITY, easing: "linear" },
    );

    return () => animation.cancel();
  }, [active, distance]);

  return (
    <span className="block overflow-hidden">
      <span ref={ref} className={active && distance > 0 ? "block w-fit whitespace-nowrap" : "block truncate"}>
        {children}
      </span>
    </span>
  );
}

export function MenuItem({
  leading,
  children,
  description,
  trailing,
  highlighted = false,
  selected = false,
  danger = false,
  disabled = false,
  size = "md",
}: Readonly<MenuItemProps>) {
  const [hovering, setHovering] = useState(false);

  return (
    <span
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      className={[
        "flex w-full items-center gap-3 rounded-lg text-left transition-colors",
        size === "sm" ? "px-3 py-1.5 text-xs" : "px-3 py-2 text-sm",
        description ? "mb-1" : "",
        highlighted ? "bg-surface-base" : "",
        danger ? "text-error-on-subtle" : highlighted || selected ? "text-content-primary" : "text-content-secondary",
        selected ? "font-medium" : "",
        disabled
          ? "cursor-not-allowed opacity-50"
          : danger
            ? "cursor-pointer hover:bg-surface-base group-focus-visible:bg-surface-base"
            : "cursor-pointer hover:bg-surface-base hover:text-content-primary group-focus-visible:bg-surface-base group-focus-visible:text-content-primary",
      ].join(" ")}
    >
      {leading}
      <span className="min-w-0 flex-1">
        <span className="block truncate">{children}</span>
        {description && (
          <span className="block text-xs text-content-tertiary">
            <ScrollingDescription active={highlighted || hovering}>{description}</ScrollingDescription>
          </span>
        )}
      </span>
      {trailing}
    </span>
  );
}
